import { Recurrence } from '@tableau/self-service-schedules';
import * as _ from 'underscore';

import { incrementRouterSpaceReactKey } from '~src/app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { getAppStore } from '~src/app/AppStore';
import { RefreshSelfServiceScheduleDialog } from '~src/app/content/RefreshSelfServiceScheduleDialog';
import { SessionSiteSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { Messages } from '~src/codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '~src/commonInfra/contentActions/ActionHelpers';
import { IErrorDetail } from '~src/commonInfra/contentActions/ActionMessages';
import { IErrorItem } from '~src/commonInfra/contentActions/MultipleItemResult';
import { openReactAlertDialog } from '~src/commonInfra/dialogs/ReactAlertDialog';
import { ExtractEncryptionState, IDataSourceBase, IDataSourceDetailInfo } from '~src/datasource/models/IDataSourceInfo';
import { DataSources } from '~src/datasource/resources/Datasources';
import { ChangeDatasourceRefreshModeAction } from '~src/datasource/services/ChangeDatasourceRefreshModeAction';
import { ChangeDatasourceRemoteAgentAction } from '~src/datasource/services/ChangeDatasourceRemoteAgentAction';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ISchedule } from '~src/schedule/types/ISchedule';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { AlertErrorCode, CommonErrorCode, RemoteAgentErrorCode, SchedulingErrorCode, UnifiedDatasourceErrorCode, WebClientErrorCode } from '~src/ts/helpers/ErrorCodes';
import { L10n } from '~src/ts/L10n/L10n';
import { DatasourceContentType } from '~src/ts/models/ContentTypes';
import { IRemoteRefreshScheduleInfo } from '~src/ts/models/IRemoteRefreshScheduleInfo';
import { Set } from '~src/ts/models/Set';
import { RemoteRefreshSchedules } from '~src/ts/resources/RemoteRefreshSchedules';
import { RefreshModeTypes } from '~src/ts/ServerApi/types/IRemoteRefreshProperties';
import { ContentUrlForItem } from '~src/ts/services/ContentUrl';

import { CreateExtractTaskActionService, CreateExtractTasksMode, ICreateExtractTasks } from './CreateExtractTaskAction';


export namespace CreateDatasourceExtractTaskActionService {

  export function createExtractsForDatasources(datasources: Set<IDataSourceBase>, encryptionState: ExtractEncryptionState): Promise<IResult> {
    return DataSources.createExtracts(datasources.keys(), encryptionState === ExtractEncryptionState.encrypted)
      .then(result =>
        ActionHelpers.displayStandardToastsForResult(
          datasources,
          result,
          {
            success: standardActionSuccessMsg(Messages.extractCreationTasksSuccess),
            error: standardActionErrorMsg(Messages.extractCreationTasksError),
          },
          (errorItem: IErrorItem<IDataSourceBase>) => datasourceErrorDetailFactory(null)(errorItem)
        )
      );
  }


  export function forDatasource(datasource: IDataSourceBase, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    const datasourceSet = Set.singleton(datasource, (ds: IDataSourceBase) => ds.id);
    return forDatasources(datasourceSet, allowedMode);
  }

  export function forDatasources(datasources: Set<IDataSourceBase>, allowedMode: CreateExtractTasksMode): Promise<IResult> {
    if (!anyRefreshableDatasources(datasources)) {
      return showCannotRefreshErrorAlert(datasources);
    }
    const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
    const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
    const isCreateBridgeExtractRefresh = (allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient);
    if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite && !isCreateBridgeExtractRefresh) {
      return RefreshSelfServiceScheduleDialog.open(CreateExtractTaskActionService.getRefreshSSSDialogParams(datasources.toArray()));
    }

    const scheduleOnServerPrompt = (datasources.size() === 1)
      ? Messages.schedulesLabelPromptDatasource({ DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name) })
      : Messages.schedulesLabelPromptDatasources({ COUNT_LOC: L10n.localizedCount(datasources.size()) });

    const scheduleOnClientPrompt = (datasources.size() === 1)
      ? Messages.schedulesLabelPromptDatasourceOnClient({ DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name) })
      : Messages.schedulesLabelPromptDatasources({ COUNT_LOC: L10n.localizedCount(datasources.size()) });

    const refreshNowPrompt =
      Messages.extractTasksRefreshNowOnDatasourcesPrompt({
        DATASOURCE_COUNT: datasources.size(),
        DATASOURCE_NAME: L10n.elideIfLong(datasources.first().name),
        DATASOURCE_COUNT_LOC: L10n.localizedCount(datasources.size()),
      });

    if ((allowedMode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.AllowScheduleOnServerAndClient ||
      allowedMode === CreateExtractTasksMode.AllowScheduleOnlyOnClient)
      && datasources.size() === 1) {
      const datasource: IDataSourceDetailInfo = datasources.first() as IDataSourceDetailInfo;
      const scheduleRefreshOnClientAction = (createExtractTasks: ICreateExtractTasks, id: string, schedule: IRemoteRefreshScheduleInfo) => {
        const finalScheduleInfo: IRemoteRefreshScheduleInfo = {
          id: '',
          contentType: 'remoteRefreshSchedule',
          definition: '',
          refreshType: createExtractTasks.remoteRefreshTypeOption.refreshType,
          scheduleDetails: createExtractTasks.selfServiceSchedulePaneProps.recurrenceModel.recurrenceDetails,
          scheduleType: createExtractTasks.selfServiceSchedulePaneProps.recurrenceModel.recurrenceType
        };

        // We should show success toasts from the change mode action if the requested refresh mode is Server,
        // because the change agent action does nothing, including showing a toast, if so.
        const showSuccessToast = createExtractTasks.remoteRefreshPropertiesEditModel.refreshMode === RefreshModeTypes.Server;

        return ChangeDatasourceRefreshModeAction.execute(datasource, createExtractTasks.remoteRefreshPropertiesEditModel.refreshMode, createExtractTasks.remoteRefreshPropertiesEditModel.remoteAgentName, !showSuccessToast, false).then((result: any) => {
          return ChangeDatasourceRemoteAgentAction.execute(datasource, createExtractTasks.remoteRefreshPropertiesEditModel.remoteAgentName, createExtractTasks.remoteRefreshPropertiesEditModel.refreshMode, false).then((result: any) => {
            return RemoteRefreshSchedules.create(datasource.id, finalScheduleInfo).finally(() => {
              getAppStore().dispatch(incrementRouterSpaceReactKey());
            });
          });
        });
      };
      const scheduleRefreshActionOnClientReact = (refreshType: string, recurrence: Recurrence, remoteAgentName: string) => {
        const finalScheduleInfo: IRemoteRefreshScheduleInfo = {
          id: '',
          contentType: 'remoteRefreshSchedule',
          definition: '',
          refreshType: refreshType,
          scheduleDetails: recurrence.recurrenceDetails,
          scheduleType: recurrence.recurrenceType
        };

        // We should show success toasts from the change mode action if the requested refresh mode is Server,
        // because the change agent action does nothing, including showing a toast, if so.
        const showSuccessToast = false; //refreshMode === RefreshModeTypes.Server;

        return ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Remote, remoteAgentName, !showSuccessToast, false).then(result => {
          return ChangeDatasourceRemoteAgentAction.execute(datasource, remoteAgentName, RefreshModeTypes.Remote, false).then(result => {
            return RemoteRefreshSchedules.create(datasource.id, finalScheduleInfo).finally(() => {
              getAppStore().dispatch(incrementRouterSpaceReactKey());
            });
          });
        });
      };

      const scheduleRefreshOnServerAction = (ids: string[], scheduleId: string, type: string) => {
        if (datasource.usingRemoteQueryAgent) {
          return DataSources.createExtractTask(ids, scheduleId, type).then((result: any) => {
            return ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, datasource.remoteAgentName, false, false).finally(() => {
              getAppStore().dispatch(incrementRouterSpaceReactKey());
            });
          });
        } else {
          return DataSources.setUsingRemoteQueryAgent(datasource.id).then((result: any) => {
            return DataSources.createExtractTask(ids, scheduleId, type).then((result: any) => {
              return ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, datasource.remoteAgentName, false, false).finally(() => {
                getAppStore().dispatch(incrementRouterSpaceReactKey());
              });
            });
          });
        }
      };

      return CreateExtractTaskActionService.execute(
        datasources,
        scheduleOnServerPrompt,
        scheduleOnClientPrompt,
        scheduleRefreshOnServerAction,
        scheduleRefreshActionOnClientReact,
        refreshNowPrompt,
        (ids: string[], type: string) => DataSources.refreshExtracts(ids, type),
        (schedule: ISchedule) => datasourceErrorDetailFactory(schedule),
        allowedMode
      );
    } else { // Keep the old behavior

      const scheduleRefreshOnServerAction = (ids: string[], scheduleId: string, type: string) => {
        return DataSources.createExtractTask(ids, scheduleId, type);
      };

      return CreateExtractTaskActionService.execute(
        datasources,
        scheduleOnServerPrompt,
        scheduleOnClientPrompt,
        scheduleRefreshOnServerAction,
        () => Promise.resolve({}),
        refreshNowPrompt,
        (ids: string[], type: string) => DataSources.refreshExtracts(ids, type),
        (schedule: ISchedule) => datasourceErrorDetailFactory(schedule),
        allowedMode
      );
    }
  }

  export function anyRefreshableDatasources(datasources: Set<IDataSourceBase>) {
    return _.any(datasources.toArray(), (ds: IDataSourceBase) => isRefreshableDatasource(ds));
  }

  export function isRefreshableDatasource(datasource: IDataSourceBase) {
    // To be refreshable, a datasource must be:
    //   1. Not embedded in a workbook
    //   2. Have an extract

    // Embedded datasources are not refreshable
    if (_.isObject(datasource.workbook)) {
      return false;
    }

    // Live datasources are not refreshable
    if (!datasource.hasExtracts) {
      return false;
    }

    return true;
  }

  export function showCannotRefreshErrorAlert(datasources: Set<IDataSourceBase>) {

    // Determine which message to display

    const allEmbedded = datasources.all((ds: IDataSourceBase) => _.isObject(ds.workbook));
    const anyEmbedded = datasources.any((ds: IDataSourceBase) => _.isObject(ds.workbook));

    let message = Messages.refreshExtracts_cannotRefreshAlertMessage_allLive();
    if (allEmbedded) {
      message = Messages.refreshExtracts_cannotRefreshAlertMessage_allEmbedded();
    } else if (anyEmbedded) {
      message = Messages.refreshExtracts_cannotRefreshAlertMessage_mixedEmbeddedAndLive();
    }

    const dialog = openReactAlertDialog({
      title: Messages.refreshExtracts_cannotRefreshAlertTitle(),
      message
    });
    return dialog.result;
  }

  export function datasourceErrorDetailFactory(schedule: ISchedule) {
    return (errorItem: IErrorItem<IDataSourceBase>): IErrorDetail => {
      return {
        itemName: errorItem.item.name,
        itemUrl: ContentUrlForItem(errorItem.item, DatasourceContentType),
        errorMessage: datasourceErrorMessageFor(errorItem, schedule)
      };
    };
  }

  export function datasourceErrorMessageFor(errorItem: IErrorItem<IDataSourceBase>, schedule: ISchedule): string {
    switch(errorItem.error.code) {
      case WebClientErrorCode.TaskTypeRefreshExtractNotAllowed:
        return Messages.extractTasksDatasourceNoExtract();
      case WebClientErrorCode.TaskTypeIncrementExtractNotAllowed:
        return Messages.extractTasksDatasourceNoIncrementalExtract();
      case SchedulingErrorCode.SchedulingExtractTaskExistsForTarget:
        return Messages.extractTasksRefreshAlreadyExistsForDatasource({
          SCHEDULE: schedule.name
        });
      case SchedulingErrorCode.SchedulingNotAllowedInPersonalSpace:
        return Messages.extractTasksRefreshNotAllowedInPersonalSpace();
      case CommonErrorCode.CallingUserInsufficientPermissions:
        return Messages.extractTasksDatasourceErrorInsufficientPermissions();
      case UnifiedDatasourceErrorCode.OperationOnlySupportedForPublishedDatasource:
        return Messages.errorOperationOnlySupportedForPublishedDatasource();
      case RemoteAgentErrorCode.NoAgentAssignedToDataSource:
        return Messages.extractTasksDatasourceErrorRemoteAgentNotAssigned();
      case RemoteAgentErrorCode.AgentNotConnected:
        return Messages.extractTasksDatasourceErrorRemoteAgentNotConnected();
      case RemoteAgentErrorCode.AgentNeedsUpdateToSupportFeature:
        return Messages.extractTasksDatasourceErrorRemoteAgentNeedsUpdate();
      case AlertErrorCode.AlertSignInFailed:
        return Messages.credentialMissedOrWrong();
      case AlertErrorCode.AlertOperationCanceled:
        return Messages.jobCancelled();
    }
    return CreateExtractTaskActionService.extractErrorMessageFor(errorItem, schedule);
  }
}
